import Bowser from 'bowser'
import { defineStore } from 'pinia'

import { Namespaces } from '@shared/models/enums/namespaces'
import { StoreState } from '@shared/models/interfaces/store'
import { Nullable } from '@shared/models/types/primary'
import { get } from '@shared/plugins/api'
import { supportedBrowsers } from '@shared/utils/constants/video-call-compatibility'
import { CompatibilityFailure } from '@shared/utils/models/video-call-compatibility'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { VideoChatCompatibilityItem } from '@protos/apps/pc/response/compatibility/video_chat_compatibility_item_pb'

type CompatibilityNamespace = Namespaces.PC | Namespaces.VC

type State = {
  compatibilityMatrix: StoreState<VideoChatCompatibilityItem>
  parser: Bowser.Parser.Parser
}

const state = (): State => ({
  compatibilityMatrix: getInitialState(),
  parser: Bowser.getParser(navigator.userAgent),
})

export const useVideoCallCompatibilityStore = defineStore(
  'video-call-compatibility',
  {
    state,
    actions: {
      async getCompatibility(namespace: CompatibilityNamespace) {
        try {
          this.compatibilityMatrix = updateState(this.compatibilityMatrix)

          const { data: response } = await get(
            '/compatibilities/get_video_chat_compatibility',
            {
              namespace,
            }
          )

          const data = VideoChatCompatibilityItem.fromJson(response)

          this.compatibilityMatrix = updateState(this.compatibilityMatrix, data)
        } catch (e) {
          this.compatibilityMatrix = updateState(
            this.compatibilityMatrix,
            e as Error
          )

          throw e
        }
      },

      // We only need to fetch once, but we need to be sure it gets called
      async ensureCompatabilityIsFetched(namespace: CompatibilityNamespace) {
        if (!this.compatibilityMatrix.data?.data) {
          return await this.getCompatibility(namespace)
        }
      },
    },
    getters: {
      satisfactionHash(state) {
        if (!state.compatibilityMatrix.data?.data) {
          return {}
        }

        const { browsers, operatingSystems } =
          state.compatibilityMatrix.data.data

        const browsersMap = browsers.reduce(
          (acc, cur) => ((acc[cur.name] = cur.version), acc),
          {} as Record<string, string>
        )

        return operatingSystems.reduce(
          (acc, cur) => {
            const { name, browsers: osBrowsers } = cur

            const osHash = osBrowsers.reduce(
              (acc, cur) => {
                acc[cur] = browsersMap[cur]
                return acc
              },
              {} as Record<string, string>
            )

            acc[name] = osHash
            return acc
          },
          {} as Record<string, Record<string, string>>
        )
      },

      blacklistedDevices(state) {
        return state.compatibilityMatrix.data?.data?.blacklistedDevices ?? []
      },

      satisfiesBowser(): boolean {
        // If we don't know for sure, let it pass
        return this.parser.satisfies(this.satisfactionHash) ?? true
      },

      satisfiesDeviceBlackList(): boolean {
        return !this.blacklistedDevices.some(({ agentTest }) => {
          return new RegExp(agentTest).test(navigator.userAgent)
        })
      },

      supportedIfSafari(): boolean {
        // We check iOS specifically for some custom error message stuff.
        if (!this.parser.is('iOS')) {
          return true
        }

        // If we do not know for sure, let it pass
        return this.parser.satisfies(this.satisfactionHash) ?? true
      },

      isCompatible(): boolean {
        return (
          this.satisfiesBowser &&
          this.satisfiesDeviceBlackList &&
          this.supportedIfSafari
        )
      },

      reasonForFailure(): Nullable<CompatibilityFailure> {
        if (this.isCompatible) {
          return null
        }

        if (!this.supportedIfSafari) {
          return CompatibilityFailure.SafariOutdated
        }

        if (!this.satisfiesDeviceBlackList) {
          return CompatibilityFailure.DeviceUnsupported
        }

        if (!this.satisfiesBowser) {
          return CompatibilityFailure.BrowserOutdated
        }

        return CompatibilityFailure.BrowserUnsupported
      },

      virtualBackgroundSupported(): boolean {
        // TODO Use backend for this, too
        const browserName = this.parser.getBrowserName()

        // TODO Use the .satisfies() Bowser method for versions
        // TODO Figure out what versions are valid
        return supportedBrowsers.includes(browserName)
      },
    },
  }
)
